import React, { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, Send } from "lucide-react";
import { useT } from "@/context/LangContext";

export default function BecomePartnerPage() {
  const { t } = useT();
  const [form, setForm] = useState({
    first_name: "", last_name: "", business_name: "", phone: "", email: "",
    gdpr_consent: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.gdpr_consent) {
      toast.error(t("become.consent_error"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/submissions", form);
      setSubmitted(true);
      toast.success(t("become.success_toast"));
    } catch (err) {
      toast.error(t("become.generic_error"));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="App">
        <Header />
        <main className="pt-32 pb-20">
          <div className="container-app max-w-2xl text-center">
            <div className="w-20 h-20 rounded-full bg-lime-500 mx-auto flex items-center justify-center mb-6">
              <CheckCircle2 className="w-10 h-10 text-navy-900" />
            </div>
            <h1 className="font-heading font-extrabold text-4xl md:text-5xl text-navy-900 tracking-tighter" data-testid="submission-success-title">
              {t("become.success.title")}
            </h1>
            <p className="mt-5 text-lg text-muted-foreground">{t("become.success.body")}</p>
            <a href="/" className="btn-navy mt-8" data-testid="submission-back-home">
              {t("become.success.back")} <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <Header />
      <main className="pt-28 md:pt-36 pb-20">
        <section className="container-app max-w-2xl">
          <div className="mb-10">
            <div className="inline-block chip bg-lime-500/15 text-navy-900 mb-4">{t("become.badge")}</div>
            <h1 className="font-heading font-extrabold text-5xl md:text-6xl text-navy-900 tracking-tighter leading-[1]" data-testid="become-partner-title">
              {t("become.title")}
            </h1>
            <p className="mt-5 text-lg text-muted-foreground">{t("become.subtitle")}</p>
          </div>

          <form onSubmit={submit} className="space-y-5 card-premium p-6 md:p-8" data-testid="become-partner-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t("become.field.first_name")}>
                <Input data-testid="input-first-name" required value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="h-12" />
              </Field>
              <Field label={t("become.field.last_name")}>
                <Input data-testid="input-last-name" required value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="h-12" />
              </Field>
            </div>
            <Field label={t("become.field.business")}>
              <Input data-testid="input-business-name" required value={form.business_name} onChange={(e) => set("business_name", e.target.value)} className="h-12" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t("become.field.phone")}>
                <Input data-testid="input-phone" type="tel" required value={form.phone} onChange={(e) => set("phone", e.target.value)} className="h-12" placeholder="+32 …" />
              </Field>
              <Field label={t("become.field.email")}>
                <Input data-testid="input-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="h-12" />
              </Field>
            </div>

            <label className="flex items-start gap-3 cursor-pointer pt-2">
              <Checkbox data-testid="input-gdpr" checked={form.gdpr_consent} onCheckedChange={(v) => set("gdpr_consent", !!v)} />
              <span className="text-sm text-navy-900 leading-snug">{t("become.consent.gdpr")}</span>
            </label>

            <button type="submit" disabled={loading} className="btn-lime w-full text-base py-4 disabled:opacity-60" data-testid="submit-become-partner">
              {loading ? t("become.sending") : <>{t("become.submit")} <Send className="w-4 h-4" /></>}
            </button>

            <p className="text-xs text-muted-foreground text-center leading-relaxed pt-2">
              {t("become.footnote")}
            </p>
          </form>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-navy-900/70 font-semibold mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
